import { z } from 'zod';

import { IsoDateTimeSchema, NullableIsoDateTimeSchema } from '../common/datetime';
import { TimezoneSchema } from '../common/fields';
import {
  CompanyId,
  SiteId,
  TerminalActivationCodeId,
  TerminalAutonomousPeriodId,
  TerminalId,
  TerminalSessionId,
} from '../common/ids';
import { CursorQuerySchema, pageSchema } from '../common/pagination';
import { DevicePlatform } from '../staff/devices';
import { TERMINAL_ACTIVATION_CODE_PATTERN } from '../terminal/activation';

import { SiteStatus } from './sites';
import { ReasonSchema } from './workers';

// ---------------------------------------------------------------------------
// Enums (contracts/admin-terminals.md; data-model.md · terminals, terminal_sessions,
// terminal_activation_codes, terminal_autonomous_periods)
// ---------------------------------------------------------------------------

/** Estado derivado en lectura, nunca almacenado (`modules/terminals/status.ts`). */
export const TerminalStatus = z.enum(['ONLINE', 'OFFLINE', 'DISABLED']);
export type TerminalStatus = z.infer<typeof TerminalStatus>;

/**
 * `ADMIN` | `SITE_INACTIVE` cuando `status = DISABLED` por `disabled_at`; `NEVER_ACTIVATED` cuando
 * `activated_at IS NULL` (FR-001, el Admin lo etiqueta "Sin activar"); `null` en `ONLINE`/`OFFLINE`.
 */
export const TerminalStatusReason = z.enum(['ADMIN', 'SITE_INACTIVE', 'NEVER_ACTIVATED']).nullable();
export type TerminalStatusReason = z.infer<typeof TerminalStatusReason>;

/** Último transporte reportado en el heartbeat (research R-1). */
export const TerminalTransport = z.enum(['ws', 'sse']);
export type TerminalTransport = z.infer<typeof TerminalTransport>;

export const TerminalActivationCodeStatus = z.enum(['PENDING', 'USED', 'EXPIRED', 'REVOKED']);
export type TerminalActivationCodeStatus = z.infer<typeof TerminalActivationCodeStatus>;

export const AutonomousPeriodSourceSchema = z.enum(['REPORTED', 'INFERRED']);
export type AutonomousPeriodSource = z.infer<typeof AutonomousPeriodSourceSchema>;

// ---------------------------------------------------------------------------
// Campos
// ---------------------------------------------------------------------------

export const TERMINAL_NAME_MAX_LENGTH = 60;
/** 1–60, recortado; único por sede. */
export const TerminalNameSchema = z.string().trim().min(1).max(TERMINAL_NAME_MAX_LENGTH);

// ---------------------------------------------------------------------------
// Entidades
// ---------------------------------------------------------------------------

/** Sesión `ACTIVE` tal como la ve el admin (solo la activa; `null` si no hay). */
export const TerminalSessionSummarySchema = z.object({
  id: TerminalSessionId,
  platform: DevicePlatform,
  browser: z.string().nullable(),
  transport: TerminalTransport.nullable(),
  started_at: IsoDateTimeSchema,
  last_heartbeat_at: NullableIsoDateTimeSchema,
  last_connected_at: NullableIsoDateTimeSchema,
  /** Offset reportado por el marcador (telemetría, nunca verdad). */
  clock_offset_ms: z.number().int().nullable(),
  /** `autonomous_key_delivered_at IS NOT NULL` para la versión vigente. */
  autonomous_key_delivered: z.boolean(),
});
export type TerminalSessionSummary = z.infer<typeof TerminalSessionSummarySchema>;

/** Código más reciente: `{ id, status, expires_at }` (un `PENDING` vencido se muestra `EXPIRED` sin escribir). */
export const TerminalActivationCodeSummarySchema = z.object({
  id: TerminalActivationCodeId,
  status: TerminalActivationCodeStatus,
  expires_at: IsoDateTimeSchema,
});
export type TerminalActivationCodeSummary = z.infer<typeof TerminalActivationCodeSummarySchema>;

/**
 * Código de activación en claro. **Solo** aparece en la respuesta que lo emite (`POST /terminals`,
 * `…/activation-code`, `…/unlink`, `…/enable`); nunca se persiste ni se loguea.
 */
export const ActivationCodeSchema = z.object({
  id: TerminalActivationCodeId,
  /** 6 símbolos de `TERMINAL_ACTIVATION_CODE_ALPHABET`. */
  code: z.string().regex(TERMINAL_ACTIVATION_CODE_PATTERN),
  expires_at: IsoDateTimeSchema,
  ttl_minutes: z.number().int().positive(),
});
export type ActivationCode = z.infer<typeof ActivationCodeSchema>;

/** Sede del terminal: `{ id, name, timezone, status }`. */
export const TerminalSiteRefSchema = z.object({
  id: SiteId,
  name: z.string(),
  timezone: TimezoneSchema,
  status: SiteStatus,
});
export type TerminalSiteRef = z.infer<typeof TerminalSiteRefSchema>;

/** Período autónomo abierto (`ended_at IS NULL`, solo `INFERRED`), o `null`. */
export const TerminalOpenAutonomousPeriodSchema = z.object({
  id: TerminalAutonomousPeriodId,
  started_at: IsoDateTimeSchema,
});
export type TerminalOpenAutonomousPeriod = z.infer<typeof TerminalOpenAutonomousPeriodSchema>;

export const TerminalSchema = z.object({
  id: TerminalId,
  company_id: CompanyId,
  site: TerminalSiteRefSchema,
  name: z.string(),
  status: TerminalStatus,
  status_reason: TerminalStatusReason,
  last_heartbeat_at: NullableIsoDateTimeSchema,
  online_since: NullableIsoDateTimeSchema,
  offline_since: NullableIsoDateTimeSchema,
  activated_at: NullableIsoDateTimeSchema,
  disabled_at: NullableIsoDateTimeSchema,
  /** Solo la sesión `ACTIVE`, o `null`. */
  session: TerminalSessionSummarySchema.nullable(),
  /** El código más reciente (cualquier estado), o `null` si nunca se emitió uno. */
  activation_code: TerminalActivationCodeSummarySchema.nullable(),
  open_autonomous_period: TerminalOpenAutonomousPeriodSchema.nullable(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
});
export type Terminal = z.infer<typeof TerminalSchema>;

export const AutonomousPeriodSchema = z.object({
  id: TerminalAutonomousPeriodId,
  terminal_id: TerminalId,
  session_id: TerminalSessionId,
  started_at: IsoDateTimeSchema,
  ended_at: NullableIsoDateTimeSchema,
  /** `null` mientras el período está abierto. */
  duration_s: z.number().int().nonnegative().nullable(),
  /** Del `autonomous_report`; `null` si nunca llegó. */
  frames_emitted: z.number().int().nonnegative().nullable(),
  /** Lo incrementa la feature 004 al verificar cada marca autónoma. */
  marks_count: z.number().int().nonnegative(),
  source: AutonomousPeriodSourceSchema,
});
export type AutonomousPeriod = z.infer<typeof AutonomousPeriodSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** `POST /terminals`: `{ name: 1–60, site_id }` (sede `ACTIVE`, de la empresa y en scope). */
export const CreateTerminalBodySchema = z.object({
  name: TerminalNameSchema,
  site_id: SiteId,
});
export type CreateTerminalBody = z.infer<typeof CreateTerminalBodySchema>;

/** `PATCH /terminals/{id}`: `{ name }`. */
export const UpdateTerminalBodySchema = z.object({
  name: TerminalNameSchema,
});
export type UpdateTerminalBody = z.infer<typeof UpdateTerminalBodySchema>;

/**
 * `GET /terminals?site_id&status&q&cursor&limit`. `site_id` inexistente/ajena/fuera de scope no es
 * error (`200` con `items: []`); `status` filtra sobre el estado derivado; `q` es prefijo de nombre.
 */
export const TerminalListQuerySchema = CursorQuerySchema.extend({
  site_id: SiteId.optional(),
  status: TerminalStatus.optional(),
  q: z.string().min(1).max(TERMINAL_NAME_MAX_LENGTH).optional(),
});
export type TerminalListQuery = z.infer<typeof TerminalListQuerySchema>;

/** Parámetro de ruta `{id}` de terminal. */
export const TerminalIdParamsSchema = z.object({ id: TerminalId });
export type TerminalIdParams = z.infer<typeof TerminalIdParamsSchema>;

/** `POST /terminals/{id}/disable`: `{ reason? }` — cuerpo JSON obligatorio (mínimo `{}`). */
export const DisableTerminalBodySchema = z.object({
  reason: ReasonSchema.optional(),
});
export type DisableTerminalBody = z.infer<typeof DisableTerminalBodySchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/** `200 Page<Terminal>` (`ORDER BY id ASC`, `next_cursor` = último `id`). */
export const TerminalListResponseSchema = pageSchema(TerminalSchema);
export type TerminalListResponse = z.infer<typeof TerminalListResponseSchema>;

/** `201 { terminal, activation_code }` de `POST /terminals`. */
export const CreateTerminalResponseSchema = z.object({
  terminal: TerminalSchema,
  activation_code: ActivationCodeSchema,
});
export type CreateTerminalResponse = z.infer<typeof CreateTerminalResponseSchema>;

/** `201 ActivationCode` de `POST /terminals/{id}/activation-code`. */
export const ActivationCodeResponseSchema = ActivationCodeSchema;
export type ActivationCodeResponse = z.infer<typeof ActivationCodeResponseSchema>;

/**
 * `200 { terminal, activation_code | null }` de `POST /terminals/{id}/unlink`.
 * `activation_code: null` cuando el terminal está `DISABLED` (solo revoca la sesión).
 */
export const UnlinkTerminalResponseSchema = z.object({
  terminal: TerminalSchema,
  activation_code: ActivationCodeSchema.nullable(),
});
export type UnlinkTerminalResponse = z.infer<typeof UnlinkTerminalResponseSchema>;

/**
 * `200 { terminal, activation_code | null }` de `POST /terminals/{id}/enable`.
 * Código solo si no hay sesión `ACTIVE`; con sesión, la pantalla recupera el standby.
 */
export const EnableTerminalResponseSchema = z.object({
  terminal: TerminalSchema,
  activation_code: ActivationCodeSchema.nullable(),
});
export type EnableTerminalResponse = z.infer<typeof EnableTerminalResponseSchema>;

/** `200 Page<AutonomousPeriod>` (`ORDER BY started_at DESC, id DESC`). */
export const AutonomousPeriodListResponseSchema = pageSchema(AutonomousPeriodSchema);
export type AutonomousPeriodListResponse = z.infer<typeof AutonomousPeriodListResponseSchema>;
